import React from 'react';
import { bool, object } from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { propTypes } from '../../util/types';
import {
  buildCityPageSchema,
  extractFaqsFromBlocks,
} from '../../util/seoDiscoverySchema';
import SeoBreadcrumbs from '../../components/SeoDiscovery/SeoBreadcrumbs';
import SeoContentSections from '../../components/SeoDiscovery/SeoContentSections';
import SeoUpcomingEvents from '../../components/SeoDiscovery/SeoUpcomingEvents';
import SeoFaqSection from '../../components/SeoDiscovery/SeoFaqSection';
import useHeroImagePosition from '../../components/SeoDiscovery/useHeroImagePosition';
import SeoHeroImagePreload, {
  heroImageSrcSet,
  HERO_IMAGE_SIZES,
} from '../../components/SeoDiscovery/SeoHeroImagePreload';
import css from '../../components/SeoDiscovery/SeoDiscovery.module.css';

const rootUrl = () =>
  typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').replace(/\/$/, '');

const cityLabel = city => [city?.city, city?.state].filter(Boolean).join(', ');

const sectionKeyOf = block =>
  block?.content?.sectionKey || block?.blockKey || block?.content?.key || null;

/** Split hub prose so venue intro sits immediately above the venue link list. */
const partitionCityHubBlocks = contentBlocks => {
  const sections = (contentBlocks || []).filter(
    b => b.blockType === 'section' || b.blockType === 'hero'
  );
  const intro = [];
  const venueIntro = [];
  const relatedIntro = [];
  sections.forEach(block => {
    const key = sectionKeyOf(block);
    if (key === 'venue_directory') venueIntro.push(block);
    else if (key === 'related_cities') relatedIntro.push(block);
    else if (key === 'event_discovery') {
      // Legacy: do not render written upcoming-events prose; live widget owns that.
    } else {
      intro.push(block);
    }
  });
  return { intro, venueIntro, relatedIntro };
};

const absoluteUrl = url => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${rootUrl()}${url.startsWith('/') ? '' : '/'}${url}`;
};

const citySocialSharing = (city, title, description) => {
  const imageUrl = absoluteUrl(city?.image?.url);
  if (!imageUrl) {
    return { title, description };
  }
  const imageAlt = city.image?.alt || `${city.city || 'City'} skyline`;
  return {
    title,
    description,
    images1200: [{ name: imageAlt, url: imageUrl, width: 1200, height: 630 }],
    images600: [{ name: imageAlt, url: imageUrl, width: 600, height: 314 }],
  };
};

const CityHeroCredit = ({ image }) => {
  if (!image?.credit && !image?.attribution) return null;
  const label = image.attribution || `Photo: ${image.credit}`;
  const licenseBit =
    image.license && image.license !== 'unsplash'
      ? ` · ${String(image.license).toUpperCase()}`
      : '';
  return (
    <p className={css.heroCredit}>
      {image.creditUrl ? (
        <a href={image.creditUrl} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      ) : (
        label
      )}
      {image.licenseUrl ? (
        <>
          {' '}
          (
          <a href={image.licenseUrl} target="_blank" rel="noopener noreferrer">
            {image.license || 'license'}
          </a>
          )
        </>
      ) : (
        licenseBit
      )}
    </p>
  );
};

export const SeoDiscoveryCityPageComponent = props => {
  const { payload, fetchInProgress, fetchError, staticContext } = props;
  const { ref: heroImageRef, position: heroImagePosition } = useHeroImagePosition();
  const city = payload?.city;
  const venues = payload?.venues || [];
  const upcomingEvents = payload?.upcomingEvents || [];
  const relatedCities = payload?.relatedCities || [];
  const contentBlocks = payload?.contentBlocks || [];
  const internalLinks = payload?.internalLinks || [];
  const breadcrumbs = payload?.breadcrumbs || [];
  const meta = payload?.meta || {};
  const page = payload?.page;
  const locationLabel = city?.locationLabel || cityLabel(city);
  const noIndex = page?.indexState && page.indexState !== 'index';
  const pageTitle = meta.title || meta.metaTitle || `Events in ${locationLabel} | PromoTix`;
  const pageDescription =
    meta.description ||
    meta.metaDescription ||
    `Find tickets for upcoming events in ${locationLabel} on PromoTix.`;
  const { intro, venueIntro, relatedIntro } = partitionCityHubBlocks(contentBlocks);
  const faqs = extractFaqsFromBlocks(contentBlocks);
  const canonicalPath =
    page?.canonicalPath || `/event-discovery/cities/${city?.citySlug || ''}/`;
  const schema = city
    ? buildCityPageSchema({
        city,
        canonicalPath,
        rootUrl: rootUrl(),
        breadcrumbs,
        faqs,
        venues,
        upcomingEvents,
        meta: {
          ...meta,
          metaTitle: pageTitle,
          metaDescription: pageDescription,
        },
      })
    : null;

  if (fetchError?.status === 404) {
    if (staticContext) staticContext.notfound = true;
    return (
      <Page title="City not found | PromoTix">
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <h1 className={css.h1}>City not found</h1>
            <p>
              <a href="/event-discovery/cities/">Browse all cities</a>
            </p>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      socialSharing={citySocialSharing(city, pageTitle, pageDescription)}
      schema={schema}
      scrollingDisabled={false}
    >
      {noIndex ? (
        <Helmet>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
      ) : null}
      <SeoHeroImagePreload url={city?.image?.url} />
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {fetchInProgress && !city ? <p>Loading…</p> : null}
          {fetchError && fetchError.status !== 404 ? <p>Unable to load city page.</p> : null}
          {city ? (
            <>
              {breadcrumbs.length ? <SeoBreadcrumbs items={breadcrumbs} /> : null}
              <header className={css.hero}>
                {city.image?.url ? (
                  <>
                    <div className={css.heroImageFrame} ref={heroImageRef}>
                      {/* LCP element: preloaded in head, high fetch priority, responsive
                          srcset where the host supports resizing (Unsplash). */}
                      <img
                        className={`${css.heroImage} ${css.heroImageActive}`}
                        src={city.image.url}
                        srcSet={heroImageSrcSet(city.image.url) || undefined}
                        sizes={heroImageSrcSet(city.image.url) ? HERO_IMAGE_SIZES : undefined}
                        alt={city.image.alt || `${city.city} skyline`}
                        loading="eager"
                        fetchpriority="high"
                        width={city.image.width || undefined}
                        height={city.image.height || undefined}
                        style={{ objectPosition: `50% ${heroImagePosition}%` }}
                      />
                    </div>
                    <CityHeroCredit image={city.image} />
                  </>
                ) : null}
                <div className={css.heroCopy}>
                  <h1 className={css.h1}>{meta.h1 || `Events in ${city.city}`}</h1>
                  {locationLabel ? <p className={css.location}>{locationLabel}</p> : null}
                </div>
              </header>
              <SeoContentSections blocks={intro} excludeHero />
              {internalLinks.filter(l => l.placement === 'hub_nav').length ? (
                <nav className={css.section} aria-label="City pages">
                  <ul className={css.indexList}>
                    {internalLinks
                      .filter(l => l.placement === 'hub_nav')
                      .map(l => (
                        <li key={l.linkId || l.targetPath}>
                          <a href={l.targetPath}>{l.anchorText}</a>
                        </li>
                      ))}
                  </ul>
                </nav>
              ) : null}
              <SeoUpcomingEvents
                events={upcomingEvents}
                title={`Upcoming Events in ${city.city}`}
                emptyMessage={`There are no live listings in ${locationLabel} right now. Check back soon or browse events on PromoTix.`}
              />
              <SeoContentSections blocks={venueIntro} />
              {venues.length ? (
                <section className={css.section}>
                  {!venueIntro.length ? <h2>Venues in {city.city}</h2> : null}
                  <ul className={css.indexList}>
                    {venues.map(v => (
                      <li key={v.venueId}>
                        <a href={v.path}>{v.venueName}</a>
                        {v.eventCount ? (
                          <span className={css.eventMeta}> — {v.eventCount} events</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              <SeoContentSections blocks={relatedIntro} />
              {relatedCities.length ? (
                <section className={css.section}>
                  {!relatedIntro.length ? <h2>Related cities</h2> : null}
                  <ul className={css.indexList}>
                    {relatedCities.map(c => (
                      <li key={c.path}>
                        <a href={c.path}>Events in {c.city}</a>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              <SeoFaqSection blocks={contentBlocks} />
              <p>
                <a href="/event-discovery/cities/">Browse all cities</a>
              </p>
            </>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoDiscoveryCityPageComponent.propTypes = {
  payload: object,
  fetchInProgress: bool,
  fetchError: propTypes.error,
  staticContext: object,
};

const mapStateToProps = state => state.SeoDiscoveryCityPage || {};

export default connect(mapStateToProps)(SeoDiscoveryCityPageComponent);
